/*
 * Copyright (c) 2014-2024 Comparatio USA  LLC. <https://www.comparatio.com>
 */

import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Route, Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {permissions} from '../../../permissions';
import {OrderHeadersService} from '../../../services/order-headers.service';
import {ShipmentHeadersService} from '../../../services/shipment-headers.service';
import {Page} from '../../../model/page';
import {ShipmentLinesModalComponent} from '../../shipment-headers/shipment-lines-modal/shipment-lines-modal.component';
import {MatDialog} from '@angular/material';
import {OrderLineService} from '../../../services/order-line.service';
import {OrderHeadersFaqModalComponent} from '../order-headers-faq-modal/order-headers-faq-modal.component';
import {InvoicingLinesModalComponent} from '../../invoicing-headers/invoicing-lines-modal/invoicing-lines-modal.component';
import {InvoicingHeadersService} from '../../../services/invoicing-headers.service';
import {ConfirmationService} from 'primeng/api';
import {environment} from '../../../../environments/environment';
import {AuthService} from '../../../auth/auth.service';
import {Title} from '@angular/platform-browser';
import {Location} from '@angular/common';
import {Subscription} from 'rxjs/Subscription';

@Component({
  selector: 'app-order-headers-view',
  templateUrl: './order-headers-view.component.html',
  styleUrls: ['./order-headers-view.component.scss'],
  providers: [
      OrderHeadersService,
      OrderLineService,
      ShipmentHeadersService,
      InvoicingHeadersService,
      AuthService
  ]
})
export class OrderHeadersViewComponent implements OnInit, OnDestroy {

    @ViewChild('myTable')   table: any;

    public loading = false;
    private editable = false;
    public  permissions = permissions;
    private onCancelResetFilters = true;

    po: any = [];
    rows = [];
    cols = [];
    totalRecords = 0;
    selected = [];

    shipmentCols = [];
    shipmentRows = [];
    shipmentTotalRecords = 0;

    invoiceCols = [];
    invoiceRows = [];
    invoiceTotalRecords = 0;

    private PO1_ID: string;

    // private units: Array<any> = [];
    // filteredUnits = [];
    private errors = [];
    private subscription: Subscription;

    public headerStatuses = this.orderHeadersService.getStatuses();
    public refOrderHeadersService = OrderHeadersService;

    private lineStatuses = this.orderLineService.getStatuses();
    private refOrderLineService = OrderLineService;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        public authService: AuthService,
        private orderHeadersService: OrderHeadersService,
        private orderLineService: OrderLineService,
        private shipmentHeadersService: ShipmentHeadersService,
        private invoicingHeadersService: InvoicingHeadersService,
        private toastr: ToastrService,
        public  dialog: MatDialog,
        private confirmationService: ConfirmationService,
        private title: Title,
        private location: Location
    ) {
        const queryParams = this.route.snapshot.queryParams;
        if (queryParams.hasOwnProperty('keepFilters') && (Number(queryParams['keepFilters']) === 1)) {
            this.onCancelResetFilters = false;
        }
        this.title.setTitle('EDI - Purchase Order');
        this.shipmentCols = [
            { field: 'SH1_INTERNAL_ID',      header: 'Shipment Number',  visible: true,  width: 300 },
            { field: 'SH1_CREATED_ON',       header: 'Created On',       visible: true,  width: 100 },
            { field: 'SH1_TRACKING_NUMBER',  header: 'Tracking Number',  visible: true,  width: 300 },
            { field: 'SH1_CREATED_BY',       header: 'Created By',       visible: true,  width: 100 }
        ];

        this.invoiceCols = [
            { field: 'IN1_NUMBER',           header: 'Invoice Number',   visible: true,  width: 200 },
            { field: 'IN1_CREATED_ON',       header: 'Created On',       visible: true,  width: 100 },
            { field: 'IN1_FREIGHT',          header: 'Freight',          visible: true,  width: 100 },
            { field: 'IN1_SALES_TAX',        header: 'Sales Tax',        visible: true,  width: 100 },
            { field: 'PO1_CURRENCY',         header: 'Currency',         visible: true,  width: 100 },
            { field: 'IN1_CREATED_BY',       header: 'Created By',       visible: true,  width: 100 }
        ];
    }

    ngOnInit() {

        this.cols = [
            { field: 'PO2_LINE',          align: 'left',
                bigScreen:   {header: 'Line #', visible: true, width: '65px'},
                smallScreen: {header: 'Line#',  visible: true, width: '50px'} },
            { field: 'PO2_STATUS',        align: 'left',
                bigScreen:   {header: 'Status', visible: true,  width: '110px'},
                smallScreen: {header: 'Status', visible: false, width: '110px'} },
            { field: 'ITEM_INFO',         align: 'left',
                bigScreen:   {header: 'Item Info', visible: true,  width: '35%'},
                smallScreen: {header: 'Item Info', visible: false, width: '140px'} },
            { field: 'ITEM_INFO_COMBINE', align: 'left',
                bigScreen:   {header: 'Item Info', visible: false, width: '140px'},
                smallScreen: {header: 'Item Info', visible: true,  width: '50%'} },
            { field: 'PO2_DESCRIPTION',   align: 'left',
                bigScreen:   {header: 'Item Desc', visible: true,  width: '35%'},
                smallScreen: {header: 'Item Desc', visible: false, width: '140px'} },
            { field: 'QUANTITY',          align: 'center',
                bigScreen:   {header: 'Quantity', visible: true, width: '20%'},
                smallScreen: {header: 'Quantity', visible: true, width: '50%'} },
            { field: 'PO2_UNIT_PRICE',    align: 'right',
                bigScreen:   {header: 'Unit Price', visible: true, width: '10%'},
                smallScreen: {header: 'Unit Price', visible: true, width: '85px'} },
            { field: 'PO2_UM',          align: 'center',
                bigScreen:   {header: 'Unit of Measure', visible: true, width: '15%'},
                smallScreen: {header: 'UM',              visible: true, width: '60px'} },
            { field: 'EXT_VAL',           align: 'right',
                bigScreen:   {header: 'Ext. Value', visible: true, width: '15%'},
                smallScreen: {header: 'Ext. Val',   visible: true, width: '90px'} },
            { field: 'PO1_CURRENCY',    align: 'center',
                bigScreen:   {header: 'Currency', visible: true, width: '100px'},
                smallScreen: {header: 'Currency', visible: true, width: '100px'} }
        ];
        this.onWindowResize();

        this.editable = true;
        this.po.PO1_ID = this.route.snapshot.params.id;
        this.po.HASH   = this.route.snapshot.params.hash;
        this.PO1_ID = this.po.PO1_ID || this.po.HASH;

        // this.orderLineService.preloadData(false).subscribe(data => {
        //     this.units = data.units.map(unit => {
        //         return {
        //             UM1_ID   : unit.UM1_ID,
        //             UM1_NAME : unit.UM1_NAME
        //         };
        //     });
        //     // GET DATA if using preload
        // });
        this.getData(this.PO1_ID);

        // const page = new Page();
        // page.pageNumber = 0;
        // page.filterParams = {
        //     PO1_ID: this.PO1_ID
        // };
        // this.shipmentHeadersService.getItems(page).subscribe(result => {
        //     this.shipmentRows = result.data;
        //     this.shipmentTotalRecords = result.count;
        // }, error => {
        //     window.dispatchEvent(new Event('resize'));
        //     this.toastr.error('TimeOut occured', 'Service Error', {
        //         timeOut: 3000,
        //     });
        // });
        //
        // this.invoicingHeadersService.getItems(page).subscribe(result => {
        //     this.invoiceRows = result.data;
        //     this.invoiceTotalRecords = result.count;
        // }, error => {
        //     window.dispatchEvent(new Event('resize'));
        //     this.toastr.error('TimeOut occured', 'Service Error', {
        //         timeOut: 3000,
        //     });
        // });
    }

    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    // autoCompleteUnitName(ev) {
    //     this.filteredUnits = ev ? this._filterUnits(ev) : this.units.slice();
    // }

    getData(id) {
        this.loading = true;
        this.subscription = this.orderHeadersService.getById(id).subscribe(res => {
            this.po = res.data;

            // res.lines.forEach(line => {
            //     line.UM1_NAME = this.getUnitName(line.UM1_ID_NEW);
            // });

            this.rows = res.lines;
            this.shipmentRows = res.shipments;
            this.shipmentTotalRecords = this.shipmentRows.length;
            this.invoiceRows = res.invoices;
            this.invoiceTotalRecords = this.invoiceRows.length;
            this.totalRecords = this.rows.length;
            this.loading = false;
        }, error => {
            this.loading = false;
            window.dispatchEvent(new Event('resize'));
            this.toastr.error('TimeOut occured', 'Service Error', {
                timeOut: 3000,
            });
        });
    }

    // getUnitName(id) {
    //     const unit = this.units.find(x => Number(x.UM1_ID) === Number(id));
    //     return (unit === undefined) ? '' : unit.UM1_NAME;
    // }

    onKeyDown(e, rowIndex, elPrefix ) {
        if ( (e.keyCode === 9 || e.keyCode === 13) ) {
            e.stopPropagation();
            const i = rowIndex + 1;
            let elem = <HTMLInputElement>document.getElementById(elPrefix + i);
            if (elem) {
                elem.focus();
                elem.select();
            } else {
                elem = <HTMLInputElement>document.getElementById(elPrefix + rowIndex);
                elem.blur();
            }
        }
    }

    onCancel() {
        if (this.onCancelResetFilters) {
            this.router.navigate(['order-headers']);
        } else {
            this.location.back();
        }
    }

    onResend() {
        this.confirmationService.confirm({
          header: 'Resend Action',
          accept: () => {
              this.orderHeadersService.resend( this.PO1_ID ).subscribe( result => {
                  let message = '';
                  result.results.forEach( item => {
                      if (item.success) {
                          message += 'PO #' + item.PO1_NUMBER + ' was successfully resent.'
                      }
                  });
                  this.errors = [];
                  this.toastr.success(message);
              });
          },
          message: 'Are you sure you wish to Resend this Purchase Order?'
      });
    }

    openFAQ() {
        this.dialog.open(OrderHeadersFaqModalComponent, {
            width: '75%',
            height: 'fit-content',
            role: 'dialog'
        });
    }

    onWindowResize() {
        this.cols.forEach(col => {
            const isBigScreen = window.innerWidth >= environment.smallScreenThreshold;
            col.visible = isBigScreen ? col.bigScreen.visible : col.smallScreen.visible;
            col.header = isBigScreen ? col.bigScreen.header : col.smallScreen.header;
            col.width = isBigScreen ? col.bigScreen.width : col.smallScreen.width;
        });
    }

    printAsPDF() {
        const page = new Page();
        page.filterParams['PO1_IDs'] = this.po.PO1_ID;
        this.orderHeadersService.printPDF( page ).subscribe(result => {
            this.toastr.success('PDF generated!', 'Success');
        }, error => {
            this.toastr.error('Error encountered while printing PDF.', 'Service Error');
        });
    }

    resetErrors() {
        this.errors = [];
    }

    selectionConfirm() {
        this.router.navigate(['order-headers/confirm/' + this.po.PO1_ID ]);
    }

    selectionReject() {
        const acknowledgedLines = this.rows.filter(row => {
            return row.PO2_STATUS <= this.refOrderLineService.STATUS_ACKNOWLEDGED;
        });
        if (acknowledgedLines.length > 0) {
            this.router.navigate(['order-headers/reject/' + this.po.PO1_ID]);
        } else {
            this.toastr.error('No lines eligible to be rejected.', 'Error', {
                timeOut: 3000
            });
        }
    }

    selectionShip() {
        const lines = this.rows.map( item => item.PO2_ID );
        const shippedLines = this.rows.filter( item => {
            return item.PO2_TOTAL_SHIPPED >= item.PO2_QUANTITY;
        });
        if (shippedLines.length < lines.length) {
            this.router.navigate(['shipment-headers/add/' + this.po.PO1_ID ]);
        } else {
            this.toastr.error('All lines are already shipped', 'Error', {
                timeOut: 3000,
            });
        }
    }

    selectionInvoice() {
        const lines = this.rows.map( item => item.PO2_ID );
        const invoicedLines = this.rows.filter( item => {
            return item.PO2_TOTAL_INVOICED >= item.PO2_QUANTITY;
        });
        if (invoicedLines.length < lines.length) {
            this.router.navigate(['invoicing-headers/add/' + this.po.PO1_ID ]);
        } else {
            this.toastr.error('All lines are already invoiced', 'Error', {
                timeOut: 3000,
            });
        }
    }

    // _filterUnits(value: string) {
    //     const filterValue = value.toUpperCase();
    //     return this.units.filter(unit => unit.UM1_NAME.toUpperCase().indexOf(filterValue) === 0);
    // }

    viewShipment(SH1_INTERNAL_ID) {
        this.router.navigate(['shipment-headers/view/' + SH1_INTERNAL_ID]);
    }

    viewInvoice(IN1_INTERNAL_ID) {
        this.router.navigate(['invoicing-headers/view/' + IN1_INTERNAL_ID]);
    }

    loadShipments(PO2_ID) {
        const line = this.rows.find( row =>  {
            return Number(row.PO2_ID) === Number(PO2_ID);
        });
        const dialogRef = this.dialog.open(ShipmentLinesModalComponent, {
          width: '90%',
          height: (window.innerHeight - 80) + 'px',
          role: 'dialog',
          data: {
              title: line.PO2_ITEM_ID + ' (' + line.PO2_DESCRIPTION1 + ')',
              PO1_ID: this.PO1_ID, PO2_ID: PO2_ID
          }
        });
    }

    loadInvoices(PO2_ID) {
        const line = this.rows.find( row =>  {
            return Number(row.PO2_ID) === Number(PO2_ID);
        });
        const dialogRef = this.dialog.open(InvoicingLinesModalComponent, {
          width: '90%',
          height: (window.innerHeight - 80) + 'px',
          role: 'dialog',
          data: {
              title: line.PO2_ITEM_ID + ' (' + line.PO2_DESCRIPTION1 + ')',
              PO1_ID: this.PO1_ID, PO2_ID: PO2_ID
          }
        });
    }

    deleteRecord() {
        this.confirmationService.confirm({
            message: 'Are you sure that you want to perform this action?',
            accept: () => {
                this.loading = true;
                this.orderHeadersService.deleteMultiple([this.po.PO1_ID]).subscribe( () => {
                    this.router.navigate(['order-headers']);
                });
            }
        });
    }

    register() {
        this.router.navigate(['user/register/' + this.authService.userId ]);
    }
}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/views/order-headers/order-headers-view/order-headers-view.component.ts